import { Injectable } from '@angular/core';
import { SurveyService } from '../../../../../core/services/survey/survey.service';
import { M1Member, RequestParameters } from '@tangilla/tg-ng-models';
import { NrdsAssociationsApiService, NrdsMembersApiService } from '@tangilla/tg-ng-core';
import { firstValueFrom } from 'rxjs';
import { M1SearchRecordsService } from './m1-search-records.service';
import { ErrorsService } from 'src/app/core/services/utils/errors.service';

@Injectable({
  providedIn: 'root'
})
export class M1SearchSingleRecordService {
  constructor(private surveyService: SurveyService,
              private nrdsAssociationsApiService: NrdsAssociationsApiService,
              private nrdsMembersApiService: NrdsMembersApiService,
              private m1SearchRecordsService: M1SearchRecordsService,
              private errorsService: ErrorsService) {
  }

  async processSingleRecord(m1Member: M1Member, nrdsSearch: string, model: any) {
    if (this.lastNameDoesntMatch(m1Member) && this.isLicenseSearch(nrdsSearch)) {
      this.showLicenseLastNameError(model);
    } else if (this.isDeceased(m1Member)) {
      this.showDiseasedError(model);
    } else {
      if (this.isMemberSecondary()) {
        await this.processMemberSecondarySingleRecord(m1Member.member_nrds_id);
      }

      await this.getAssociation(m1Member);
    }
  }

  private async processMemberSecondarySingleRecord(member_nrds_id: string) {
    try {
      const memberResponse = (
        await firstValueFrom(
          this.nrdsMembersApiService.getAll((new RequestParameters(null))
            .setAdditionalFields({member_nrds_id}))
        ) as any
      );
      const memberData = memberResponse.data;

      this.processMemberSupplemental(memberData);
    } catch (error) {
      this.errorsService.processError(error, 'soft');
    }
  }

  private processMemberSupplemental(m1Member: any) {
    const memberSupplemental = this.getMemberDataMemberSupplemental(m1Member);
    const surveyAssociationNrdsId = this.surveyService.surveyModel.getVariable('v-association_nrds');

    if (memberSupplemental) {
      const {exitTitle, exitMessage} = this.getMemberSupplementalExitObject(m1Member);

      this.errorsService.exitForm(exitTitle, exitMessage);
    } else if (surveyAssociationNrdsId === m1Member.association_nrds_id) {
      let terminatedhtml = `We are unable to proceed with your Secondary membership application as you are currently a Primary member with ${m1Member.association_nrds_id}.<br/><br/>`;
      terminatedhtml += `If you would like to be a Secondary member with ${m1Member.association_nrds_id}, you will need to join another Association as Primary member.<br/><br/>`;
      this.errorsService.exitForm('You are currently a Primary Member', terminatedhtml);
    }

  }

  private getMemberDataMemberSupplemental(member: any) {
    return member.member_supplemental && member.member_supplemental.find((memberSupplemental: any) => {
      const isAffiliateApplication = !!this.surveyService.surveyModel.getQuestionByName('CorporateAffiliate');

      const isSameAssociation = memberSupplemental.association_nrds_id === this.surveyService.surveyModel.getVariable('v-association_nrds');

      const isActive = ['A', 'P'].includes(memberSupplemental.status);

      const differentTypeOfApplication =
        (isAffiliateApplication && memberSupplemental.member_type !== 'AFF') ||
        (!isAffiliateApplication && memberSupplemental.member_type === 'AFF');

      return isSameAssociation && isActive && !differentTypeOfApplication;
    });
  }

  private getMemberSupplementalExitObject(member: M1Member) {
    const firstName = member.first_name;
    const localAssociation = this.surveyService.surveyModel.getVariable('v-association');
    const exitTitle = `Hi ${firstName} !`;
    const exitMessage = `You are already an active ${localAssociation} member. <br /><br />` +
      `What a you trying to do today ? <br />` +
      `<li><strong>Applying for a new MLS Only Subscription?</strong> <a href="javascript:window.location.reload()">Start this form again</a> and select MLS ONLY</li>` +
      `<li><strong>Changing Brokerages?</strong> You will need to complete a <u>Change Brokerage Form</u></li>` +
      `<li><strong>Applying for a DR Transfer?</strong> You will need to complete a <u>DR Transfer Form</u>`;

    return {exitTitle, exitMessage};
  }

  private lastNameDoesntMatch(m1Member: M1Member) {
    const surveyLastName = this.surveyService.surveyModel.getQuestionByName('LastName')?.value;

    return m1Member.last_name !== surveyLastName;
  }

  private isLicenseSearch(nrdsSearch: string) {
    return nrdsSearch !== 'LIC';
  }

  private showLicenseLastNameError(model: any) {
    const surveyLastName = this.surveyService.surveyModel.getQuestionByName('LastName')?.value;

    this.surveyService.surveyModel.setVariable('nrds_request_processing', false);
    this.surveyService.surveyModel.setVariable('nrds_validated', false);
    model.addError('The last name for that NRDS number does not match yours (' + surveyLastName + ').');
    // $('#NRDSsearchError').html(survey.getVariable('nrds_request_error')).show();
  }

  private showDiseasedError(model: any) {
    this.surveyService.surveyModel.setVariable('nrds_validated', false);
    this.surveyService.surveyModel.setVariable('nrds_request_processing', false);
    model.addError('We have not been able to find a NRDS number for you.');
  }

  private isDeceased(m1Member: M1Member) {
    return m1Member.status === 'X';
  }

  private isMemberSecondary() {
    return this.surveyService.surveyModel.getQuestionByName('MembershipType').value === 'Secondary';
  }

  private async getAssociation(m1Member: any) {
    try {
      const data = await firstValueFrom(this.nrdsAssociationsApiService.getAll((new RequestParameters(null))
        .setAdditionalFields({association_nrds_id: m1Member.association_nrds_id})));
      this.processAssociation(data, m1Member);
    } catch (error) {
      this.processAssociationError(error);
    }
  }

  private processAssociation(response: any, m1Member: any) {
    const association = response[0]; // TODO: Check this, as I see it should return an array
    const formattedBusinessName = association.business_name.replace(/[^ -~]+/g, '');
    this.surveyService.surveyModel.setVariable('PrimaryLocalAssociation', formattedBusinessName);

    this.m1SearchRecordsService.checkMemberStatus(m1Member, formattedBusinessName);

    this.m1SearchRecordsService.mapQuestionJsonChoices(m1Member);

    this.surveyService.surveyModel.setVariable('nrds_request_processing', false);
    // $('#NRDSsearchError').html('').hide();
    this.surveyService.surveyModel.setVariable('nrds_validated', true);
  }

  private processAssociationError(error: any) {
    /*process_errors(error, survey);*/
  }
}
